import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';

import api from '@/services/api';
import FileSaver from 'file-saver';

import Pagination from '@/utils/pagination/Pagination';

import Header from '@/views/components/Header/Header';
import Footer from '@/views/components/Footer/Footer';
import DictionarySearchFilters from '@/views/pages/Dictionary/elements/DictionarySearchFilters';
import DictionaryContainer from '@/views/pages/Dictionary/elements/DictionaryContainer';

import parseObjectToQuery from '@/utils/functions/parseObjectToQuery';
import { CheckPermission } from '@/utils/permissions/permissions';

import { DictionaryModalContext } from '@/utils/contexts/DictionaryModalContext';

import ModalDeleteItem from '@/views/pages/Dictionary/modals/ModalDeleteItem';
import ModalDeleteDictionary from '@/views/pages/Dictionary/modals/ModalDeleteDictionary';
import ModalLoadDictionary from '@/views/pages/Dictionary/modals/ModalLoadDictionary';

import styles from '@/views/pages/Dictionary/Dictionary.module.scss';

// SORT:
// DICT_ENTRIES_ORD_MAP.put("inputPhrase", "+");
// DICT_ENTRIES_ORD_MAP.put("outputPhrase", "+");
// DICT_ENTRIES_ORD_MAP.put("basicInputPhrase", "+");
// DICT_ENTRIES_ORD_MAP.put("basicOutputPhrase", "+");

const Dictionary = () => {
	const { dict, dictionaryExport } = useSelector((state) => state.app);
	const { user } = useSelector((state) => state.auth);
	const location = useLocation();
	const navigate = useNavigate();

	const [ dictData, setDictData ] = useState(null);
	const [ pagesNum, setPagesNum ] = useState(null);
	const [ noRecords, setNoRecords ] = useState(false);
	const [ lettersNum, setLettersNum ] = useState(0);

	const [ sortMethods, setSortMethods ] = useState([
		{ value: 'inputPhrase', label: dict.sortOptions.originalPhrase },
		{ value: '-inputPhrase', label: dict.sortOptions.originalPhraseReverse },
		{ value: 'outputPhrase', label: dict.sortOptions.simplification },
		{ value: '-outputPhrase', label: dict.sortOptions.simplificationReverse },
	]);

	const [ showModalDeleteDictionary, setShowModalDeleteDictionary ] = useState(false);
	const [ showModalLoadDictionary, setShowModalLoadDictionary ] = useState(false);

	const [ sortOn, setSortOn ] = useState(sortMethods[0]);
	const [ isLoading, setIsLoading ] = useState(true);

	const addDictionaryContext = useContext(DictionaryModalContext);

	// modalDefaultValues - used in conjuction with addNewModalVisible causes that modal fires in "edit mode" (when it is null modal is in "add mode")
	// const [ modalDefaultValues, setModalDefaultValues ] = useState(null);

	// this variable conditions the appearance of delete modal
	const [ itemToDelete, setItemToDelete ] = useState(null);

	const { search: locationSearch, pathname: locationPathName } = location;
	
	const changeSort = useCallback((element) => {
		const searchParams = queryString.parse(locationSearch);
		const copiedSearchParams = Object.assign({}, searchParams);
	
		setSortOn(element);
		navigate(`${locationPathName}?${parseObjectToQuery({ ...copiedSearchParams, ord: element.value })}`);
	}, [ setSortOn, locationSearch, locationPathName ]);

	useEffect(() => {
		const searchParams = queryString.parse(location.search);
		const copiedSearchParams = Object.assign({}, searchParams);

		if (copiedSearchParams.q) {
			const newSortMethods = [
				{ value: '-accuracy', label: dict.sortOptions.accuracy },
				{ value: 'inputPhrase', label: dict.sortOptions.originalPhrase },
				{ value: '-inputPhrase', label: dict.sortOptions.originalPhraseReverse },
				{ value: 'outputPhrase', label: dict.sortOptions.simplification },
				{ value: '-outputPhrase', label: dict.sortOptions.simplificationReverse },
			];
			setSortMethods(newSortMethods);
		} else {
			const newSortMethods = [
				{ value: 'inputPhrase', label: dict.sortOptions.originalPhrase },
				{ value: '-inputPhrase', label: dict.sortOptions.originalPhraseReverse },
				{ value: 'outputPhrase', label: dict.sortOptions.simplification },
				{ value: '-outputPhrase', label: dict.sortOptions.simplificationReverse },
			];
			setSortMethods(newSortMethods);
		}
	}, [ location ]);

	useEffect(() => {
		const searchParams = queryString.parse(location.search);
		const copiedSearchParams = Object.assign({}, searchParams);
		if (copiedSearchParams.ord) {
			setSortOn(sortMethods.find((el) => { return el.value === copiedSearchParams.ord; }));
		} else {
			setSortOn(sortMethods[0]);
		}
	}, [ sortMethods ]);

	useEffect(() => () => {
		// on unmount Dictionary clear reloadDictionaryCallback from context
		addDictionaryContext.setReloadDictionaryCallback(null);
		addDictionaryContext.setModalVisible(false);
	}, []);

	const fetchData = ({ data }) => {
		if (data) {
			setDictData(data.dictEntries);
			setPagesNum(parseInt(data.pages));
			setLettersNum(data.hits);
		}
	};
	const showAddModal = () => {
		addDictionaryContext.setModalVisible(true);
	};

	const reloadDictionary = useCallback(() => {
		// if there is no page in location don't fetch data
		const searchParams = queryString.parse(location.search);

		setIsLoading(true);
		api.get(`/dict-entries?${parseObjectToQuery(searchParams)}`).then(res => {
			fetchData(res);
			setIsLoading(false);
		});
		
	}, [ location ]);

	useEffect(() => {
		const qu = Object.fromEntries(new URLSearchParams(location.search));

		const noData = (dictData && dictData.length === 0 && qu && qu.q === undefined) ? true : false;
		setNoRecords(noData);
	}, [ location, dictData ]);
		
	useEffect(() => {
		addDictionaryContext.setReloadDictionaryCallback(reloadDictionary);
	}, [ reloadDictionary ]);

	useEffect(() => {
		reloadDictionary();
	}, [ location ]);

	const onDeleteItem = (data) => {
		setItemToDelete(data);
	};

	const onEditItem = (data) => {
		addDictionaryContext.setEditValues({
			inputPhrase: data.inputPhrase || '',
			outputPhrase: data.outputPhrase || '',
			basicInputPhrase: data.basicInputPhrase || '',
			basicOutputPhrase: data.basicOutputPhrase || '',
			comments: data.comments || '',
			id: data.id,
		});
		addDictionaryContext.setModalVisible(true);
	};

	const getXlsx = () => {
		// const qu = Object.fromEntries(new URLSearchParams(location.search));
		api.get(`/dict-entries/export/excel${location.search}`, {
			responseType: 'blob',
		}).then((response) => {
			let fileName = response.headers["content-disposition"].split("filename=")[1];
			fileName = fileName.replaceAll('"','');
			FileSaver.saveAs(response.data, fileName);
		});
	};

	return (
		<div className={ styles['dictionary__wrapper'] }>
			<Header narrow={ false } />
			{itemToDelete && (
				<ModalDeleteItem
					itemToDelete={ itemToDelete }
					setItemToDelete={ setItemToDelete }
					reloadDictionary={ reloadDictionary }
				/>
			)}
			<ModalDeleteDictionary
				reloadDictionary={ reloadDictionary } 
				showModalDeleteDictionary={ showModalDeleteDictionary } 
				setShowModalDeleteDictionary={ setShowModalDeleteDictionary }
			/>
			<ModalLoadDictionary 
				reloadDictionary={ reloadDictionary } 
				showModalLoadDictionary={ showModalLoadDictionary } 
				setShowModalLoadDictionary={ setShowModalLoadDictionary } 
			/>
			
			<main className={ styles['dictionary__main'] }>
				<div className={ styles['dictionary__header-container'] }>
					<h1 className={ styles['dictionary__header'] }>{dict.dictionary.dictionary}
						{lettersNum > 0 && (
							<span className={ styles['dictionary__header--number'] }>&nbsp;({lettersNum})</span>)}
					</h1>
					{CheckPermission("canManageWholeDictionary", user.role) && !noRecords && <button 
						className={ `button__transparent ${noRecords ? 'disabled' : ''}` }
						style={ { padding: 0 } }
						disabled={ noRecords }
						onClick={ () => setShowModalDeleteDictionary(true) }><i className='icon-delete'/>{dict.dictionary.deleteDict}</button> 
					}
				</div>
				<div className={ styles['dictionary__description'] }>
					<div className={ styles['dictionary__description--text'] }>
						{ !CheckPermission("canManageWholeDictionary", user.role) && <div>{dict.dictionary.description}</div> }
						{ CheckPermission("canManageWholeDictionary", user.role) && <div>{dict.dictionary.descriptionAdmin}</div> }
					</div>
					{(CheckPermission("canManageWholeDictionary", user.role) || CheckPermission("canEditDictionary", user.role)) && (
						<div className={ styles['dictionary__description--buttons'] }>
							{ (!dictionaryExport || noRecords) 
								? 
								null
								:
								(CheckPermission("canManageWholeDictionary", user.role) && <button
									onClick={ getXlsx }
									disabled={ noRecords }
									className={ `button__white ${noRecords ? 'disabled' : ''}` }
								><i className='icon-download' /> {dict.dictionary.download}
								</button>)}
							{CheckPermission("canManageWholeDictionary", user.role) && <button className='button__blue'
								onClick={
									() => setShowModalLoadDictionary(true)
								}
							>
								<i className='icon-upload' />
								{dict.dictionary.loadDict}
							</button>}
							{CheckPermission("canEditDictionary", user.role) && <button
								className='button__blue'
								onClick={ showAddModal }
							>+ {dict.dictionary.addToDict}
							</button>}
						</div>)}
				</div>
				
				<div className={ styles['dictionary__filters--wrapper'] }>
					<DictionarySearchFilters 
						noRecords={ noRecords }
					/>
				</div>
				<Pagination
					pagesNum={ pagesNum }
					sortingOptions={ sortMethods }
					sortingValue={ sortOn }
					sortingOnChange={ changeSort }
				>
					<DictionaryContainer
						dictData={ dictData }
						isLoading={ isLoading }
						onEditItem={ onEditItem }
						onDeleteItem={ onDeleteItem }
						noRecords={ noRecords }
					/>
				</Pagination>
			</main>

			<Footer narrow={ false } />
		</div>
	);
};
export default React.memo(Dictionary);